import ContractEligibilityService from '@/services-http/contract/ContractEligibilityService';
import ContractProductService from '@/services-http/contract/ContractProductService';
import MovementRecordService from '@/services-http/sdi/MovementRecordService';
import ContractService from '@/services-http/contract/ContractService';
import moment from 'moment';
import ReuseAttachmentMixin from './ReuseAttachmentMixin';

export default {
  name: 'FamilyGroupInclusionMixin',
  data: () => ({ moment }),
  mixins: [
    ReuseAttachmentMixin,
  ],
  methods: {
    async fetchContractsByFinancialGroupId(financialGroupId) {
      const query = `financialGroupIds=${financialGroupId}&onlyMainFields=true`;
      const contractService = new ContractService();

      return await contractService.FindAllByFilters(query)
        .then((response) => response.data)
        .then((result) => {
          let contracts = result.filter((contract) => contract.subcontract === false && !!contract.carrier_id);

          contracts = contracts
            .map((contract) => {
              const text = `${
                (
                  contract.benefit_id && contract.benefit_id.name
                    ? contract.benefit_id.name
                    : contract.benefit_id
                      ? contract.benefit_id
                      : '-'
                )
              } - ${
                (
                  contract.carrier_id && contract.carrier_id.xipp_commercial_name
                    ? contract.carrier_id.xipp_commercial_name
                    : '-'
                )
              } - ${
                contract.policy
                  ? contract.policy
                  : '-'
              } - ${
                contract.contract_owner_id && contract.contract_owner_id.legal_name
                  ? contract.contract_owner_id.legal_name
                  : '-'
              }`;

              return {
                accountID: contract.carrier_id.id,
                contract: contract.id,
                cutOffDate: contract.cut_off_date,
                text,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text, { numeric: true }));

          return contracts;
        });
    },
    async fetchSubcontractsByContractId(contractId) {
      const movementNotAllowed = '&movementNotAllowed=false';
      const query = !this.isCriticizedCarrier ? `onlyMainFields=true${movementNotAllowed}` : 'onlyMainFields=true';

      return await this.contractService.FindByParentIdByFilters(contractId, query)
        .then((response) => response.data)
        .then((contracts) => {
          if (!contracts && Array.isArray(contracts) && contracts.length === 0) return [];

          let subContracts = contracts.filter(
            (contract) => contract.subcontract === true && !!contract.carrier_id,
          );

          subContracts = subContracts
            .map((sub) => {
              const subContractNumber = sub.subcontract_number || '-';
              const subContractLegalName = (sub.contract_owner_id && sub.contract_owner_id.legal_name) || '-';
              const text = `${subContractNumber} - ${subContractLegalName}`;

              return {
                accountID: sub.carrier_id.id,
                subcontract: sub.id,
                insuranceCarrierId: sub.carrier_id.id,
                text,
              };
            })
            .sort((a, b) => a.text.localeCompare(b.text, { numeric: true }));

          return subContracts;
        }).catch((error) => console.error(error));
    },
    async fetchElegibilitiesByContractFields(contractId, subContractId) {
      const contractEligibilityService = new ContractEligibilityService();
      const query = `${contractId}?subcontractId=${subContractId}`;

      return await contractEligibilityService.FindAllByFilters(query)
        .then((response) => response.data)
        .then((result) => {
          const contractEligibilitiesMap = result.map((e) => ({
            key: e.elegibility_id.id,
            value: e.id,
          }));

          const uniqueRecords = result.reduce((acc, obj) => {
            const duplicatedIndex = acc.findIndex((it) => it.elegibility_id.id === obj.elegibility_id.id);
            if (duplicatedIndex === -1) {
              acc.push(obj);
            }
            return acc;
          }, []);

          uniqueRecords.forEach((record) => {
            const contractEligibilities = contractEligibilitiesMap.filter(
              ({ key }) => key === record.elegibility_id.id,
            ).map(({ value }) => value);
            this.$set(record.elegibility_id, 'contractEligibilities', contractEligibilities);
          });

          return uniqueRecords;
        });
    },
    async fetchPlansByElegibilityId(elegibilityId) {
      const contractProductService = new ContractProductService();
      return await contractProductService.FindAllByFilters(`?contractEligibilitiesIds=${elegibilityId}`)
        .then((response) => response.data)
        .then((result) => {
          const plans = result.map((plan) => ({
            eligibility_id: plan.eligibility_id,
            ...plan.plan_id,
            name: `${plan.plan_id.name} - ${plan.plan_id.code}`,
          }));

          return plans;
        });
    },
    async getHolderAttachments(movement) {
      const holderAttachments = await this.fetchAttachments(movement.movementRecordIdToReuse);
      const holderData = this.getHolderToReuse(movement);
      return [
        {
          ...holderData,
          documents: holderAttachments,
        },
      ];
    },
    getHolderToReuse(movement) {
      return {
        planId: parseInt(movement.plan),
        contractId: parseInt(movement.contractId),
      };
    },
    async getDependentAttachments(movement) {
      const dependentAttachments = [];
      const movementRecordService = new MovementRecordService();
      const dependentsMovements = await movementRecordService.FindDependentsByFamilyGroupId(movement.familyGroupId)
        .then((response) => response.data)
        .then((dependents) => {
          if (Array.isArray(dependents)) return dependents;
          return [];
        });

      for (const dependentMovement of dependentsMovements) {
        const attachments = await this.fetchAttachments(dependentMovement.id);
        const dependentToReuse = await this.getDependentToReuse(dependentMovement, attachments);
        dependentAttachments.push(dependentToReuse);
      }

      return dependentAttachments;
    },
    async getDependentToReuse(dependent, attachments) {
      const contracts = await this.fetchContractsByFinancialGroupId(dependent.financialGroupId);
      const documents = {
        documents: attachments,
        filesUpdates: true,
      };

      const basicInformation = {
        fullName: dependent.insuredName,
        socialInsuredName: dependent.socialInsuredName,
        document: dependent.insuredDocumentNumber,
        birthDate: dependent.birthDate
          ? moment(dependent.birthDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : null,
        maritalStatus: dependent.maritalStatus,
        gender: dependent.gender,
        socialInsuredGenderId: dependent.socialInsuredGenderId,
        relationship: dependent.degreeKinship,
        height: dependent.height,
        weight: dependent.weight,
      };
      const contactInformation = {
        email: dependent.email,
        cellphone:
          dependent.cellphoneDDD && dependent.cellphoneNumber
            ? dependent.cellphoneDDD + dependent.cellphoneNumber
            : null,
        telephone:
          dependent.homephoneDDD && dependent.homephoneNumber
            ? dependent.homephoneDDD + dependent.homephoneNumber
            : null,
      };
      const additionalInformation = {
        cns: dependent.cns,
        dnv: dependent.dnv,
        motherName: dependent.motherName,
        identityDocumentNature: dependent.natureIdentifyDocument,
        identityDocumentNumber: dependent.rgDocumentIdentify,
        identityDocumentEmissionDate: dependent.rgDocumentIssueDate,
        identityDocumentIssuer: dependent.rgDocumentIssuingBody,
        identityDocumentState: dependent.documentStateIssuer,
        identityDocumentCountry: dependent.documentCountryIssue,
        stateBirth: dependent.stateBirth,
        registrationComplement: dependent.registrationComplement,
      };
      const addressInformation = {
        city: dependent.addressCity,
        code: dependent.addressZip,
        complement: dependent.addressComplement,
        log: dependent.address,
        logType: dependent.addressType,
        neighborhood: dependent.addressNeighborhood,
        number: dependent.addressNumber,
        region: dependent.addressState,
        ibgeCode: dependent.ibgeCode,
      };

      return {
        ...basicInformation,
        ...contactInformation,
        ...additionalInformation,
        ...addressInformation,
        ...documents,
        plans: [],
      };
    },
    hasBenefitsSelected(beneficiary) {
      return beneficiary && beneficiary.plans.some((benefit) => !!benefit.contract) || false;
    },
    async mountBeneficiaryData(movement, benefits) {
      const financialGroupId = parseInt(movement.financialGroupId);
      const admissionDate = movement.eventDate
        ? moment(movement.eventDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : null;

      return {
        admissionDate,
        capacityCode: movement.taxAllocationCode,
        capacityDescription: movement.taxAllocationDescription,
        cpf: this.formatter.formatCpf(movement.insuredDocumentNumber),
        department: movement.costCenterDepartment,
        financialGroup: financialGroupId,
        name: movement.insuredName,
        socialHolderName: movement.socialHolderName,
        plans: benefits,
      };
    },
    async mountBenefitData(movement) {
      const beneficiary = JSON.parse(sessionStorage.getItem('beneficiaryContractedPlan'));
      const benefits = this.hasBenefitsSelected(beneficiary)
        ? beneficiary.plans.map((plan) => ({
          ...plan,
          allowedDates: [null],
          validityDate: plan.validityDate.includes('-')
            ? moment(plan.validityDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
            : plan.validityDate,
        }))
        : [];

      if (this.hasBenefitsSelected(beneficiary)) return benefits;

      const financialGroupId = parseInt(movement.financialGroupId);
      const contracts = await this.fetchContractsByFinancialGroupId(financialGroupId);
      const availableContracts = await this.getAvailableContracts(movement.insuredDocumentNumber, contracts, 'contract');

      return [{
        financialGroup: financialGroupId,
        contract: null,
        contracts: availableContracts,
        subcontract: null,
        subcontracts: [],
        eligibility: null,
        eligibilities: [],
        plan: null,
        plans: [],
        validityDate: '',
        allowedDates: [null],
      }];
    },
    mountComplementaryData(movement) {
      return {
        birthday: movement.birthDate,
        cellphone:
          movement.cellphoneDDD && movement.cellphoneNumber
            ? movement.cellphoneDDD + movement.cellphoneNumber
            : null,
        cns: movement.cns,
        companyDocumentNumber: movement.companyDocumentNumber,
        companyName: movement.companyName,
        dnv: movement.dnv,
        email: movement.email,
        employmentRelationship: movement.employmentRelationshipId,
        gender: movement.gender,
        socialGenderId: movement.socialGenderId,
        height: movement.height,
        identityDocumentCountry: movement.documentCountryIssue,
        identityDocumentEmissionDate: movement.rgDocumentIssueDate,
        identityDocumentIssuer: movement.rgDocumentIssuingBody,
        identityDocumentNature: movement.natureIdentifyDocument,
        identityDocumentNumber: movement.rgDocumentIdentify,
        identityDocumentState: movement.documentStateIssuer,
        job: movement.insuredPosition,
        maritalStatus: movement.maritalStatus,
        motherName: movement.motherName,
        nationality: movement.nationality,
        pisPasep: movement.pisPasep,
        registration: movement.registrationPlate,
        registrationComplement: movement.registrationComplement,
        rne: movement.rne,
        stateBirth: movement.stateBirth,
        telephone:
          movement.homephoneDDD && movement.homephoneNumber
            ? movement.homephoneDDD + movement.homephoneNumber
            : null,
        weight: movement.weight,
        workCard: movement.professionalCardNumber,
      };
    },
    mountAddressData(movement) {
      return {
        city: movement.addressCity,
        code: movement.addressZip,
        complement: movement.addressComplement,
        log: movement.address,
        logType: movement.addressType,
        neighborhood: movement.addressNeighborhood,
        number: movement.addressNumber,
        region: movement.addressState,
      };
    },
    mountBankData(movement) {
      return {
        account: movement.bankAccount,
        accountDigit: movement.bankAccountCheckDigit,
        agency: movement.branchBank,
        agencyDigit: movement.branchBankCheckDigit,
        code: movement.bank ? parseInt(movement.bank) : null,
      };
    },
  },
};
